import {Inject, injectable} from 'inversify-props';
import {CancelToken} from 'axios';

import {UpgradeStatus} from '@/model/system/update/UpgradeStatus';

import {IApiService} from '@/services/IApi.service';
import {ISystemUpgradeService} from '@/services/system/IUpgrade.service';

@injectable()
export class SystemUpgradeService implements ISystemUpgradeService {

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  private path: string = '/system/update';

  public async getChannel(): Promise<string> {
    return this.apiService.get(`${this.path}/channel`);
  }

  public async getRepository(): Promise<string> {
    return this.apiService.get(`${this.path}/repository`);
  }

  public async download(releaseUrl: string): Promise<void> {
    return this.apiService.post(`${this.path}/download`, {
      releaseUrl,
    });
  }

  public async downloadCancel(): Promise<void> {
    return this.apiService.post(`${this.path}/download-cancel`, null);
  }

  public async upload(artifactFile: File, progressionObserver: (progress: number) => void, cancellationToken: CancelToken, timeout: number = 5 * 60 * 1000): Promise<void> {
    const config = {
      timeout,
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress: (progressEvent: any) => {
        progressionObserver(progressEvent.loaded / artifactFile.size * 100);
      },
      cancelToken: cancellationToken,
    };

    try {
      await this.apiService.post(`${this.path}/send`, artifactFile, config);
    } catch (error) {
      if (error.isAxiosError && !error.response) {
        const reason = await this.apiService.get(`${this.path}/send-error`);

        let message = reason.message;

        const errors = [];
        if (reason.log && reason.log.length > 0) {
          const errorRegex = RegExp('level=error msg="(.*)"');
          for (const line of reason.log) {
            const matches = line.match(errorRegex);

            if (matches) {
              errors.push(matches[1]);
            }
          }

          message = errors.join('<br>');
        }

        throw new Error(message);
      } else {
        throw error;
      }
    }
  }

  public async status(): Promise<UpgradeStatus> {
    return this.apiService.get(`${this.path}/status`);
  }

  public async cancelDownloaded(): Promise<void> {
    return this.apiService.post(`${this.path}/downloaded-cancel`, null);
  }

  public async apply(): Promise<void> {
    return this.apiService.post(`${this.path}/apply`, null, {
      timeout: 30 * 1000,
    });
  }

  public async cancel(): Promise<void> {
    return this.apiService.post(`${this.path}/cancel`, null);
  }

  public async persist(): Promise<void> {
    return this.apiService.post(`${this.path}/persist`, null);
  }

  public async rollback(): Promise<void> {
    return this.apiService.post(`${this.path}/rollback`, null);
  }
}
